import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { PasswordField } from '../components/PasswordField';
import { PasswordStrengthMeter } from '../components/PasswordStrengthMeter';
import { passwordStrength } from '../lib/passwordStrength';
import { toast } from 'sonner';
import { Plane, Mail, User } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const { user, login, signup } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({ name: '', email: '', password: '' });

  useEffect(() => { document.title = 'Sign in | FlightDeals.in'; if (user) navigate('/account'); }, [user, navigate]);

  // Surface OAuth callback errors (e.g. redirect_uri_mismatch handled by Google, or our own).
  useEffect(() => {
    const err = new URLSearchParams(window.location.search).get('error');
    if (!err) return;
    const MAP = {
      google_not_configured: 'Google sign-in isn\'t configured yet. Please try email sign-in.',
      state_mismatch: 'Sign-in session expired. Please try again.',
      access_denied: 'Google sign-in was cancelled.',
      oauth_failed: 'Google sign-in failed. Please try again.',
      no_code: 'Google sign-in failed. Please try again.',
    };
    toast.error(MAP[err] || 'Google sign-in could not be completed.');
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const startGoogle = () => {
    // FlightDeals.in's OWN Google OAuth (server-side). The backend builds the consent URL and
    // holds the client secret; we only navigate the browser to our backend login route.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${backend}/api/auth/google/login?next=${encodeURIComponent('/')}`;
  };

  const doLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await login(loginForm); toast.success('Welcome back!'); navigate('/account'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Login failed'); }
    finally { setLoading(false); }
  };
  const doSignup = async (e) => {
    e.preventDefault();
    if (!passwordStrength(signupForm.password).valid) {
      toast.error('Password must be at least 8 characters and include a letter and a number.');
      return;
    }
    setLoading(true);
    try { await signup(signupForm); toast.success('Account created!'); navigate('/account'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Signup failed'); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-14">
        <div className="text-center mb-6">
          <span className="inline-grid place-items-center h-12 w-12 rounded-2xl bg-accent text-accent-foreground mb-3"><Plane className="h-6 w-6" /></span>
          <h1 className="font-display text-2xl font-semibold">Welcome to FlightDeals.in</h1>
          <p className="text-sm text-muted-foreground">Save searches, track prices & plan trips.</p>
        </div>

        <Button onClick={startGoogle} variant="outline" className="w-full h-11 rounded-xl mb-4" data-testid="login-google-button">
          <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4 mr-2" /> Continue with Google
        </Button>
        <div className="flex items-center gap-3 my-4"><div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">or</span><div className="h-px bg-border flex-1" /></div>

        <Tabs defaultValue="login">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="login" data-testid="tab-login">Sign in</TabsTrigger>
            <TabsTrigger value="signup" data-testid="tab-signup">Create account</TabsTrigger>
          </TabsList>
          <TabsContent value="login">
            <form onSubmit={doLogin} className="space-y-3 mt-4">
              <div><Label>Email</Label><div className="relative"><Mail className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" /><Input type="email" required className="pl-9 h-11" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} data-testid="login-email-input" /></div></div>
              <div><Label>Password</Label><PasswordField value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} autoComplete="current-password" required testid="login-password-input" /></div>
              <div className="text-right -mt-1"><Link to="/forgot-password" className="text-xs text-accent hover:underline" data-testid="forgot-password-link">Forgot password?</Link></div>
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="login-submit-button">Sign in</Button>
            </form>
          </TabsContent>
          <TabsContent value="signup">
            <form onSubmit={doSignup} className="space-y-3 mt-4">
              <div><Label>Name</Label><div className="relative"><User className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" /><Input required className="pl-9 h-11" value={signupForm.name} onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })} data-testid="signup-name-input" /></div></div>
              <div><Label>Email</Label><div className="relative"><Mail className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" /><Input type="email" required className="pl-9 h-11" value={signupForm.email} onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })} data-testid="signup-email-input" /></div></div>
              <div><Label>Password</Label><PasswordField value={signupForm.password} onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })} autoComplete="new-password" required minLength={8} testid="signup-password-input" /><PasswordStrengthMeter value={signupForm.password} testid="signup-strength" /></div>
              <Button type="submit" disabled={loading || !passwordStrength(signupForm.password).valid} className="w-full h-11 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="signup-submit-button">Create account</Button>
            </form>
          </TabsContent>
        </Tabs>
        <p className="text-center text-xs text-muted-foreground mt-6">You can search flights without signing in. <Link to="/flights" className="text-accent">Search now</Link></p>
      </div>
    </Layout>
  );
}
